import {
  ChoiceProvider,
  getChoiceCSS,
  OptionTypeBase,
  reactSelectStyles,
  selectComponents,
} from 'platform/components';
import {css, useTheme} from 'styled-components';

import {useMemo} from 'react';
import ReactSelect, {OnChangeValue} from 'react-select';

import {isNil} from 'ramda';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

import i18n from '../../../../i18n/i18n';

interface FeaturesFilterTriggerProps extends TestIdProps {
  items: Array<{key: string; label: string}>;
  isDisabled?: boolean;
  isLoading?: boolean;
  onOpen: () => void;
  onChange: (keys: string[]) => void;
}

export function FeaturesFilterTrigger(props: FeaturesFilterTriggerProps) {
  const theme = useTheme();

  const options = props.items.map((item) => ({value: item.key, label: item.label}));

  const components = useMemo(
    () => ({
      ...selectComponents<string, true>(),
      Menu: () => null,
    }),
    []
  );

  const handleChange = (nextOptions: OnChangeValue<OptionTypeBase<string | Nullish>, true>) => {
    if (!nextOptions) {
      props.onChange([]);
      return;
    }

    props.onChange(
      nextOptions.map((option) => option.value).filter((value): value is string => !isNil(value))
    );
  };

  const handleMenuOpen = () => {
    if (props.isDisabled || props.isLoading) {
      return;
    }

    props.onOpen();
  };

  return (
    <div
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.stopPropagation();
        }
      }}
    >
      <ChoiceProvider
        options={{
          'data-testid': props['data-testid'],
        }}
      >
        <ReactSelect
          isMulti
          menuIsOpen={false}
          openMenuOnClick
          onMenuOpen={handleMenuOpen}
          value={options}
          options={options}
          onChange={handleChange}
          placeholder={i18n.t('general.labels.select')}
          isDisabled={props.isDisabled}
          isLoading={props.isLoading}
          isSearchable={false}
          isClearable
          components={components}
          hideSelectedOptions={false}
          tabSelectsValue={false}
          styles={reactSelectStyles<string, true>(theme)}
          // Styling ReactSelect is problematic so we need to use the css prop
          // eslint-disable-next-line react/forbid-component-props
          css={css`
            ${getChoiceCSS(false, props.isDisabled)}

            && .eag--choice__control {
              min-height: ${theme.getSize(8)};
            }
          `}
          data-testid={suffixTestId('trigger', props)}
        />
      </ChoiceProvider>
    </div>
  );
}
